import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { PaymentTestModeBanner } from '@/components/PaymentTestModeBanner';
import { useSubscription } from '@/hooks/useSubscription';

export const Route = createFileRoute('/_authenticated/checkout/return')({
  head: () => ({ meta: [{ title: 'Welcome to Premium — LendyTrack' }] }),
  validateSearch: (search: Record<string, unknown>): { session_id?: string } => ({
    session_id: typeof search.session_id === 'string' ? search.session_id : undefined,
  }),
  component: CheckoutReturn,
});

function CheckoutReturn() {
  const { session_id } = Route.useSearch();
  const navigate = useNavigate();
  const { isPremium, refetch } = useSubscription();

  // Poll for ~10s while the webhook lands
  useEffect(() => {
    if (isPremium) return;
    let cancelled = false;
    let tries = 0;
    const id = setInterval(async () => {
      tries += 1;
      await refetch();
      if (cancelled || tries >= 10) clearInterval(id);
    }, 1000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [isPremium, refetch]);

  return (
    <div className="min-h-screen bg-gradient-surface">
      <PaymentTestModeBanner />
      <div className="mx-auto max-w-xl px-6 py-20">
        <div className="rounded-2xl border border-border bg-card p-10 text-center shadow-soft">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-success/10 text-success">
            {isPremium ? <CheckCircle2 className="h-7 w-7" /> : <Loader2 className="h-7 w-7 animate-spin" />}
          </div>
          <h1 className="mt-4 font-display text-3xl font-semibold">
            {isPremium ? "You're in." : 'Finalizing your subscription…'}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {isPremium
              ? 'Premium features are unlocked. Welcome aboard.'
              : session_id
                ? 'Payment received. Syncing your access now — this takes a few seconds.'
                : 'Thanks for subscribing.'}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button
              onClick={() => navigate({ to: '/dashboard' })}
              className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
            >
              Go to dashboard
            </button>
            <Link to="/analyzer" className="rounded-md border border-border bg-background px-4 py-2 text-sm font-semibold hover:bg-muted">
              Analyze a loan
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
